import React, { useEffect, useState, useMemo } from 'react';
import { createPortal } from 'react-dom';
import styles from './FullscreenModal.module.css';

const FullscreenModal = ({
  isOpen,
  onClose,
  title,
  subtitle,
  children,
  size = 'large',
  showProgress = false,
  progress = 0,
  isDirty = false,
  loading = false,
  // Optional fixed card height (e.g. '90vh'). When set, the content
  // card is pinned to EXACTLY this height (min + max), overriding the
  // stylesheet's default 80vh cap, so short steps don't collapse and
  // dropdowns have room to open without spilling past the card.
  // Opt-in per caller — other fullscreen modals are unaffected.
  contentHeight,
  maxWidth,
  disableBackdropClick = false
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setIsVisible(true);
      setIsAnimating(true);
      // Prevent body scroll
      document.body.style.overflow = 'hidden';

      // Add animation class after render
      setTimeout(() => setIsAnimating(false), 300);
    } else {
      setIsAnimating(true);
      setTimeout(() => {
        setIsVisible(false);
        setIsAnimating(false);
        document.body.style.overflow = 'unset';
      }, 200);
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  // Handle ESC key
  useEffect(() => {
    const handleEsc = (event) => {
      if (event.keyCode === 27 && isOpen) {
        onClose();
      }
    };
    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [isOpen, onClose]);

  if (!isVisible) return null;

  // While open → play the enter animation; while closing (rendered but
  // no longer open) → play the exit animation so the modal fades out
  // smoothly instead of abruptly disappearing.
  const phaseClass = isOpen
    ? (isAnimating ? styles.entering : '')
    : styles.exiting;

  const modalClasses = [
    styles.modalOverlay,
    size === 'fullscreen' ? styles.fullscreen : '',
    phaseClass
  ].filter(Boolean).join(' ');

  const contentClasses = [
    styles.modalContent,
    size === 'fullscreen' ? styles.fullscreen : '',
    loading ? styles.loading : '',
    phaseClass
  ].filter(Boolean).join(' ');

  return createPortal(
    <div className={modalClasses} onClick={disableBackdropClick ? undefined : onClose}>
      <div
        className={contentClasses}
        style={{
          ...(contentHeight ? { minHeight: contentHeight, maxHeight: contentHeight } : {}),
          ...(maxWidth ? { maxWidth, width: '100%' } : {})
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Modal Header */}
        <div className={`${styles.modalHeader} ${size === 'fullscreen' ? styles.fullscreen : ''}`}>
          {showProgress && (
            <div className={styles.progressBar}>
              <div
                className={styles.progressFill}
                style={{ width: `${progress}%` }}
              />
            </div>
          )}

          <div className={styles.modalTitleSection}>
            <div>
              <h2 className={styles.modalTitle}>{title}</h2>
              {subtitle && <p className={styles.modalSubtitle}>{subtitle}</p>}
            </div>

            {/* {size === 'fullscreen' && (
              <div className={styles.saveIndicator}>
                <span className={isDirty ? styles.unsaved : styles.saved}>
                  {isDirty ? '● Unsaved changes' : '✓ All changes saved'}
                </span>
              </div>
            )} */}
          </div>

          <button
            className={styles.modalCloseButton}
            onClick={onClose}
            disabled={loading}
          >
            ×
          </button>
        </div>

        {/* Modal Body */}
        <div className={`${styles.modalBody} ${size === 'fullscreen' ? styles.fullscreen : ''}`}>
          {children}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default FullscreenModal;